import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { CallButton } from "@/components/prospects/CallButton";
import { CallSummaryBadge } from "@/components/prospects/CallSummaryBadge";
import { WhatsAppButton } from "@/components/prospects/WhatsAppButton";
import { DictationTextarea } from "@/components/ui/DictationTextarea";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { TONE_STYLES } from "@/lib/colors";
import {
  OBJECTION_TONE,
  SEGMENT_LABEL,
  SEGMENT_TONE,
  chatLines,
  dayMonth,
  formatPoints,
  pctSeen,
  pickTemplate,
  rdvTimes,
  selectedIssue,
} from "@/lib/brief";
import { cn, fullName } from "@/lib/utils";
import type { GoogleEvent, ProductKey, Prospect, WebinarAttendee } from "@shared/schema";
import type { OutcomeChoice } from "@shared/outcome";
import { parseCalendlyBlock } from "@shared/calendly";
import { detectObjections } from "@shared/objections";
import type { CallSummaryView } from "@shared/webinar-api";
import { bookedAtOf } from "@shared/whatsapp";

/** Ouvre la modale d'issue (Task 10). `choice` : bouton cliqué dans la barre, pour pré-sélectionner. */
export type IssueHandler = (
  prospect: Prospect,
  eventId?: number,
  choice?: OutcomeChoice,
) => void;

/** `on` : classes du bouton choisi (plein, texte blanc), dans la couleur de l'issue. */
const ISSUE_BUTTONS: Array<{ choice: OutcomeChoice; label: string; on: string }> = [
  {
    choice: "closed_pc",
    label: "Closé Power Coaching",
    on: "border-[var(--color-success)] bg-[var(--color-success)]",
  },
  {
    choice: "closed_acc",
    label: "Closé Accélérateur",
    on: "border-[var(--color-success)] bg-[var(--color-success)]",
  },
  {
    choice: "relance",
    label: "À relancer",
    on: "border-[var(--color-warning)] bg-[var(--color-warning)]",
  },
  { choice: "lost", label: "Perdu", on: "border-[var(--color-danger)] bg-[var(--color-danger)]" },
  { choice: "no_show", label: "Absent", on: "border-[#64748b] bg-[#64748b]" },
];

/** Libellé d'un bouton choisi : « À relancer · 26/10 », « Absent · rappeler 24/10 ». */
function issueLabel(
  choice: OutcomeChoice,
  base: string,
  prospect: Pick<Prospect, "nextFollowUpAt" | "nextAction">,
): string {
  const day = dayMonth(prospect.nextFollowUpAt);
  if (choice === "relance" && day) return `${base} · ${day}`;
  if (choice === "no_show" && prospect.nextAction === "Rappeler" && day) {
    return `${base} · rappeler ${day}`;
  }
  return base;
}

function Pill({ tone, children }: { tone: keyof typeof TONE_STYLES; children: React.ReactNode }) {
  const t = TONE_STYLES[tone];
  return <span className={cn("pill", t.pillBg, t.pillFg)}>{children}</span>;
}

function meetingLabel(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/zoom\./i.test(url)) return "Zoom";
  if (/meet\.google|google\.com\/meet/i.test(url)) return "Google Meet";
  return "Visio";
}

function BlockTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-[var(--color-muted-foreground)]">
      {children}
    </h4>
  );
}

/** Notes avant l'appel : enregistrées à la sortie du champ. */
function BriefNotes({
  prospect,
  onSaved,
}: {
  prospect: Prospect;
  onSaved?: (id: number, briefNotes: string | null) => void;
}) {
  const toast = useToast();
  const field = useRef<HTMLTextAreaElement>(null);
  const saved = useRef(prospect.briefNotes ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const timer = useRef<number | undefined>(undefined);
  const autosave = useRef<number | undefined>(undefined);

  // Lit la valeur réelle du champ (texte dicté compris), jamais un état React.
  const save = useCallback(async () => {
    window.clearTimeout(autosave.current);
    const value = field.current?.value ?? saved.current;
    if (value === saved.current) return;
    const next = value.trim() ? value : null;
    saved.current = value;
    setState("saving");
    try {
      await api.updateProspect(prospect.id, { briefNotes: next });
      onSaved?.(prospect.id, next);
      setState("saved");
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setState("idle"), 2000);
    } catch (e) {
      saved.current = prospect.briefNotes ?? "";
      setState("idle");
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }, [prospect.id, prospect.briefNotes, onSaved, toast]);

  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      window.clearTimeout(autosave.current);
      // Changement de fiche ou de page : on enregistre ce qui a été dicté.
      void saveRef.current();
    },
    [],
  );

  function scheduleSave() {
    window.clearTimeout(autosave.current);
    autosave.current = window.setTimeout(() => void saveRef.current(), 1500);
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <BlockTitle>Mes notes pour l'appel</BlockTitle>
        <span
          aria-live="polite"
          className={cn(
            "mb-1.5 inline-flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] transition-opacity",
            state === "idle" ? "opacity-0" : "opacity-100",
          )}
        >
          {state === "saved" && (
            <>
              <Check size={12} /> Enregistré
            </>
          )}
          {state === "saving" && "Enregistrement…"}
        </span>
      </div>
      <DictationTextarea
        ref={field}
        className="input-base"
        rows={4}
        initialValue={prospect.briefNotes ?? ""}
        onValueChange={scheduleSave}
        onBlur={() => void save()}
        placeholder="Ce qu'il faut retenir, comment ouvrir l'appel…"
        aria-label="Mes notes pour l'appel"
      />
    </div>
  );
}

/**
 * Fiche avant l'appel (maquette #fiche) : en-tête, réponses Calendly, comportement au live, chat,
 * notes et barre d'issue. `fill` : le corps défile et la barre d'issue reste en pied (panneau de
 * la page Aujourd'hui) ; sinon la fiche prend sa hauteur naturelle (page prospect).
 */
export function CallBrief({
  prospect,
  attendee,
  event,
  bookingUrl,
  webinarHeldAt,
  onIssue,
  onNotesSaved,
  hadAttendedRdv = false,
  lastSaleProduct = null,
  callSummary = null,
  fill = false,
  hideName = false,
}: {
  prospect: Prospect;
  attendee: WebinarAttendee | null;
  event?: GoogleEvent | null;
  bookingUrl: string;
  /** Jour du live (YYYY-MM-DD), pour « Au live du 22/10 ». */
  webinarHeldAt?: string | null;
  onIssue: IssueHandler;
  onNotesSaved?: (id: number, briefNotes: string | null) => void;
  /** Au moins un RDV passé honoré : gabarit WhatsApp « relance ». */
  hadAttendedRdv?: boolean;
  /** Produit de la vente la plus récente (départage les deux « Closé » d'un client). */
  lastSaleProduct?: ProductKey | null;
  /** Dernier appel enregistré (étiquette + phrase), affiché sous l'en-tête. */
  callSummary?: CallSummaryView | null;
  fill?: boolean;
  /** Sous `sm` : n'affiche pas le nom (déjà dans le titre de la page, fiche prospect). */
  hideName?: boolean;
}) {
  const calendly = useMemo(() => parseCalendlyBlock(prospect.context), [prospect.context]);
  const chat = useMemo(() => chatLines(attendee), [attendee]);
  const objections = useMemo(
    () => detectObjections({ chat, calendly }),
    [chat, calendly],
  );
  const times = rdvTimes(event?.startTime);
  const segment = attendee?.segment ?? prospect.segment;
  const pct = pctSeen(attendee);
  const template = pickTemplate(prospect, event, undefined, hadAttendedRdv);
  const chosenIssue = selectedIssue({
    status: prospect.status,
    eventOutcome: event?.outcome ?? null,
    lastSaleProduct,
  });
  const format = meetingLabel(event?.meetingUrl);
  const live = dayMonth(webinarHeldAt);

  const subParts: string[] = [];
  if (times) {
    subParts.push(
      `Appel ${times.parisDay}`,
      `${times.local} chez toi`,
      `${times.paris} Paris`,
    );
  }
  if (format) subParts.push(format);
  if (attendee?.converted) subParts.push("RDV pris pendant le live");

  const offer = attendee
    ? attendee.clickedOffer
      ? "a cliqué sur l'offre"
      : attendee.sawOffer
        ? "a vu l'offre sans cliquer"
        : "n'a pas vu l'offre"
    : "";

  return (
    <section
      className={cn(
        "surface overflow-hidden",
        // Sous `sm` : colonne, pour remonter la barre d'issue juste après l'en-tête (`order`).
        // Dès `sm` l'ordre du DOM (en-tête, corps, barre d'issue) ne change pas.
        "max-sm:flex max-sm:flex-col",
        fill && "flex max-h-[calc(100dvh-8.5rem)] flex-col",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--color-border)] px-[18px] py-4 max-sm:order-1">
        <div className="min-w-0">
          <h3
            className={cn(
              "text-xl font-bold leading-tight text-[var(--color-navy-700)]",
              hideName && "max-sm:hidden",
            )}
          >
            {fullName(prospect)}
          </h3>
          {subParts.length > 0 && (
            <p className="mt-0.5 text-sm text-[var(--color-muted-foreground)]">
              {subParts.join(" · ")}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {segment && <Pill tone={SEGMENT_TONE[segment]}>{SEGMENT_LABEL[segment]}</Pill>}
            {objections.map((o) => (
              <Pill key={o} tone={OBJECTION_TONE[o]}>
                {o}
              </Pill>
            ))}
          </div>
          {callSummary && (
            <div className="mt-3 rounded-md border-l-4 border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2">
              <CallSummaryBadge summary={callSummary} variant="brief" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 max-sm:w-full max-sm:flex-nowrap">
          <WhatsAppButton
            prospect={prospect}
            template={template}
            rdvStart={event?.startTime}
            bookedAt={bookedAtOf(event?.rawJson)}
            bookingUrl={bookingUrl}
            className="max-sm:h-11 max-sm:flex-1"
          />
          <CallButton phone={prospect.phone} className="max-sm:h-11 max-sm:flex-1" />
        </div>
      </div>

      <div
        className={cn(
          "space-y-4 px-[18px] py-4 max-sm:order-3",
          fill && "min-h-0 flex-1 overflow-y-auto",
        )}
      >
        {calendly.length > 0 && (
          <div>
            <BlockTitle>Ses réponses Calendly</BlockTitle>
            <dl className="space-y-2 text-[13.5px]">
              {calendly.map((c, i) => (
                <div key={i}>
                  <dt className="text-[var(--color-muted-foreground)]">{c.q}</dt>
                  <dd className="font-semibold text-[var(--color-navy-700)]">{c.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div>
          <BlockTitle>{live ? `Au live du ${live}` : "Au live"}</BlockTitle>
          {attendee ? (
            <>
              <div className="text-sm">
                <b>{formatPoints(attendee.score)} pts</b> · {pct} % du live vu · {offer}
              </div>
              <div
                className="my-1.5 h-2 overflow-hidden rounded-full bg-[var(--color-muted)]"
                role="progressbar"
                aria-valuenow={pct ?? 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Part du live vue"
              >
                <i
                  className="block h-full bg-[var(--color-navy-600)]"
                  style={{ width: `${pct ?? 0}%` }}
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Pas de données du live pour cette personne.
            </p>
          )}
        </div>

        {attendee && (
          <div>
            <BlockTitle>Ce qu'il ou elle a écrit dans le chat</BlockTitle>
            <div className="max-h-52 overflow-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-[13px]">
              {chat.length === 0 ? (
                <div className="py-1 text-[var(--color-muted-foreground)]">
                  Rien écrit dans le chat.
                </div>
              ) : (
                chat.map((line, i) => (
                  <div
                    key={i}
                    className="border-b border-dashed border-[var(--color-border)] py-1 last:border-0"
                  >
                    {line}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <BriefNotes key={prospect.id} prospect={prospect} onSaved={onNotesSaved} />
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-[var(--color-border)] bg-[var(--color-surface)] px-[18px] py-3 max-sm:order-2 max-sm:grid max-sm:grid-cols-2 max-sm:border-b max-sm:border-t-0">
        <span className="mr-1 text-sm font-semibold max-sm:hidden">Issue :</span>
        <span className="text-[11px] font-bold uppercase tracking-wide text-[var(--color-muted-foreground)] sm:hidden max-sm:col-span-2">
          Issue de l'appel
        </span>
        {ISSUE_BUTTONS.map((b) => {
          const on = chosenIssue === b.choice;
          return (
            <button
              key={b.choice}
              type="button"
              aria-pressed={on}
              className={cn(
                "btn btn-sm",
                "max-sm:h-[46px] max-sm:whitespace-normal max-sm:text-center max-sm:leading-tight",
                b.choice === "no_show" && "max-sm:col-span-2",
                on
                  ? cn(b.on, "text-white hover:brightness-95")
                  : "btn-outline",
              )}
              onClick={() => onIssue(prospect, event?.id, b.choice)}
            >
              {on && <Check size={15} strokeWidth={2.5} aria-hidden />}
              {on ? issueLabel(b.choice, b.label, prospect) : b.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}
